import { useState } from 'react';
import { 
  Store, CalendarCheck, BookOpen, ChevronRight, ChevronLeft,
  Check, Sparkles, ArrowRight, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const styles = `
  .ow-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ow-fadein 0.3s ease;
  }

  @keyframes ow-fadein {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .ow-modal {
    background: #1e293b;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 90%;
    max-width: 560px;
    max-height: 90vh;
    overflow-y: auto;
    animation: ow-slidein 0.3s ease;
  }

  @keyframes ow-slidein {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ow-header {
    padding: 32px 32px 0;
    text-align: center;
  }

  .ow-header-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: linear-gradient(135deg, #c9a84c, #d4b566);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .ow-header h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    font-weight: 600;
    color: #f5f0e8;
    margin: 0 0 8px;
  }

  .ow-header p {
    font-size: 14px;
    color: rgba(245,240,232,0.5);
    margin: 0;
  }

  .ow-close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    color: rgba(245,240,232,0.4);
    cursor: pointer;
    padding: 4px;
  }

  .ow-close:hover { color: #f5f0e8; }

  .ow-body { padding: 24px 32px 32px; }

  /* Progress dots */
  .ow-dots {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin: 20px 0;
  }

  .ow-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255,255,255,0.15);
    transition: all 0.2s;
  }

  .ow-dot.active { background: #c9a84c; width: 24px; border-radius: 4px; }
  .ow-dot.done { background: #22c55e; }

  /* Feature toggles */
  .ow-feature {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    padding: 18px 20px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .ow-feature:hover { background: rgba(255,255,255,0.06); }
  .ow-feature.selected { border-color: #c9a84c; background: rgba(201,168,76,0.08); }

  .ow-feature-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ow-feature-info { flex: 1; }

  .ow-feature-title {
    font-size: 15px;
    font-weight: 700;
    color: #f5f0e8;
    margin-bottom: 2px;
  }

  .ow-feature-desc {
    font-size: 12px;
    color: rgba(245,240,232,0.45);
  }

  .ow-check {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 2px solid rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
  }

  .ow-check.on {
    background: #c9a84c;
    border-color: #c9a84c;
  }

  /* Store setup fields */
  .ow-field {
    margin-bottom: 16px;
  }

  .ow-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: rgba(245,240,232,0.5);
    margin-bottom: 6px;
  }

  .ow-input {
    width: 100%;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    padding: 12px 16px;
    color: #f5f0e8;
    font-size: 14px;
    font-family: 'Nunito Sans', sans-serif;
  }

  .ow-input:focus { outline: none; border-color: #c9a84c; }

  .ow-textarea {
    width: 100%;
    min-height: 80px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    padding: 12px 16px;
    color: #f5f0e8;
    font-size: 14px;
    font-family: 'Nunito Sans', sans-serif;
    resize: vertical;
  }

  .ow-textarea:focus { outline: none; border-color: #c9a84c; }

  /* Footer nav */
  .ow-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
  }

  /* Completion screen */
  .ow-complete {
    text-align: center;
    padding: 16px 0;
  }

  .ow-complete-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
  }

  .ow-complete h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 24px;
    color: #f5f0e8;
    margin: 0 0 12px;
  }

  .ow-complete p {
    font-size: 14px;
    color: rgba(245,240,232,0.55);
    margin: 0 0 24px;
    line-height: 1.5;
  }

  .ow-summary {
    background: rgba(255,255,255,0.04);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
    text-align: left;
  }

  .ow-summary-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    font-size: 14px;
    color: rgba(245,240,232,0.7);
  }

  .ow-summary-item svg { color: #22c55e; flex-shrink: 0; }
`;

const FEATURES = [
  { 
    key: 'storefront', 
    label: 'Storefront', 
    desc: 'Sell physical and digital products through the marketplace',
    icon: Store,
    color: '#4a9eff',
    bg: 'rgba(26,95,168,0.15)'
  },
  { 
    key: 'bookings', 
    label: 'Bookings', 
    desc: 'Accept 1:1 session bookings from members and the public',
    icon: CalendarCheck,
    color: '#a855f7',
    bg: 'rgba(106,31,168,0.15)'
  },
  { 
    key: 'workshops', 
    label: 'Workshops & Content', 
    desc: 'Create and manage workshops, modules, and educational content',
    icon: BookOpen,
    color: '#c9a84c',
    bg: 'rgba(181,98,42,0.15)'
  },
];

export default function OnboardingWizard({ onComplete, onSkip, hasVendorAccount, canEnablePractitioner = true }) {
  const [step, setStep] = useState(0);
  const [features, setFeatures] = useState({
    storefront: false,
    bookings: false,
    workshops: canEnablePractitioner,
  });
  const [storeInfo, setStoreInfo] = useState({
    store_name: '',
    store_description: '',
  });

  const toggleFeature = (key) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalSteps = features.storefront && !hasVendorAccount ? 3 : 2;
  // Steps: 0=features, 1=store setup (if storefront), last=complete

  const handleNext = () => {
    if (step === 0) {
      if (features.storefront && !hasVendorAccount) {
        setStep(1); // Go to store setup
      } else {
        setStep(totalSteps); // Skip to complete
      }
    } else if (step === 1) {
      setStep(totalSteps); // Go to complete
    }
  };

  const handleFinish = () => {
    onComplete({
      features,
      storeInfo: features.storefront && !hasVendorAccount ? storeInfo : null,
    });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="ow-overlay">
        <div className="ow-modal" style={{ position: 'relative' }}>
          <button className="ow-close" onClick={onSkip} data-testid="wizard-close">
            <X size={20} />
          </button>

          <div className="ow-header">
            <div className="ow-header-icon">
              <Sparkles size={28} color="#0f172a" />
            </div>
            {step < totalSteps ? (
              <>
                <h2>Set Up Your Practitioner Hub</h2>
                <p>Choose what you'd like to offer on 11/11 Collective Connection</p>
              </>
            ) : (
              <>
                <h2>You're All Set!</h2>
                <p>Your Practitioner Hub is ready</p>
              </>
            )}
          </div>

          {/* Progress dots */}
          {step < totalSteps && (
            <div className="ow-dots">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div 
                  key={i} 
                  className={`ow-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} 
                />
              ))}
            </div>
          )}

          <div className="ow-body">
            {/* Step 0: Feature Selection */}
            {step === 0 && (
              <>
                <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.5)', marginBottom: 16 }}>
                  Select the features you want to enable. You can change these anytime from Feature Settings.
                </p>
                {FEATURES.filter(f => canEnablePractitioner || f.key === 'storefront').map(f => {
                  const Icon = f.icon;
                  return (
                    <div 
                      key={f.key}
                      className={`ow-feature ${features[f.key] ? 'selected' : ''}`}
                      onClick={() => toggleFeature(f.key)}
                      data-testid={`wizard-toggle-${f.key}`}
                    >
                      <div className="ow-feature-icon" style={{ background: f.bg }}>
                        <Icon size={20} color={f.color} />
                      </div>
                      <div className="ow-feature-info">
                        <div className="ow-feature-title">{f.label}</div>
                        <div className="ow-feature-desc">{f.desc}</div>
                      </div>
                      <div className={`ow-check ${features[f.key] ? 'on' : ''}`}>
                        {features[f.key] && <Check size={14} color="white" />}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Step 1: Store Setup (if storefront enabled and no vendor account yet) */}
            {step === 1 && (
              <>
                <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.5)', marginBottom: 20 }}>
                  Set up your storefront basics. You can edit these later from your Practitioner Hub.
                </p>
                <div className="ow-field">
                  <div className="ow-label">Store Name</div>
                  <input 
                    className="ow-input"
                    placeholder="e.g. Crystal Harmony Shop"
                    value={storeInfo.store_name}
                    onChange={e => setStoreInfo(prev => ({ ...prev, store_name: e.target.value }))}
                    data-testid="wizard-store-name"
                  />
                </div>
                <div className="ow-field">
                  <div className="ow-label">Store Description</div>
                  <textarea 
                    className="ow-textarea"
                    placeholder="Tell customers what your store is about..."
                    value={storeInfo.store_description}
                    onChange={e => setStoreInfo(prev => ({ ...prev, store_description: e.target.value }))}
                    data-testid="wizard-store-desc"
                  />
                </div>

                <div style={{
                  marginTop: 12, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(91,181,162,0.08)", border: "1px solid rgba(91,181,162,0.15)",
                }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "rgba(91,181,162,0.9)", margin: "0 0 4px" }}>
                    Shipping Tip
                  </p>
                  <p style={{ fontSize: 12, color: "rgba(245,240,232,0.5)", margin: 0, lineHeight: 1.6 }}>
                    If you sell physical products, set the <strong style={{ color: "rgba(245,240,232,0.7)" }}>weight and dimensions</strong> on each product in your Products page. This ensures accurate shipping rates for your customers. You can also set your <strong style={{ color: "rgba(245,240,232,0.7)" }}>ship-from address</strong> in the Practitioner Hub Overview.
                  </p>
                </div>
              </>
            )}

            {/* Complete */}
            {step >= totalSteps && (
              <div className="ow-complete">
                <div className="ow-complete-icon">
                  <Check size={32} color="white" />
                </div>
                <h3>Your Hub Is Configured</h3>
                <p>
                  Here's a summary of what's been set up. You can adjust any of these from your Feature Settings at any time.
                </p>
                <div className="ow-summary">
                  {Object.entries(features).filter(([_, v]) => v).map(([key]) => {
                    const feat = FEATURES.find(f => f.key === key);
                    return feat ? (
                      <div key={key} className="ow-summary-item">
                        <Check size={16} />
                        <span>{feat.label} — enabled</span>
                      </div>
                    ) : null;
                  })}
                  {features.storefront && !hasVendorAccount && storeInfo.store_name && (
                    <div className="ow-summary-item">
                      <Check size={16} />
                      <span>Store: {storeInfo.store_name}</span>
                    </div>
                  )}
                </div>
                <Button 
                  onClick={handleFinish}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="wizard-finish"
                >
                  Open Practitioner Hub <ArrowRight size={16} className="ml-2" />
                </Button>
                <p style={{ fontSize: 12, color: 'rgba(245,240,232,0.45)', marginTop: 14, lineHeight: 1.6 }}
                   data-testid="wizard-walkthrough-note">
                  Next you'll get the <strong style={{ color: '#c9a84c' }}>setup walkthroughs</strong> —
                  screenshots of each area with the buttons marked, so you can follow along. They open
                  on their own the first time, and afterwards they live under
                  <strong style={{ color: '#c9a84c' }}> Setup Walkthroughs</strong> in your Quick Actions.
                </p>
              </div>
            )}

            {/* Footer Navigation */}
            {step < totalSteps && (
              <div className="ow-footer">
                {step > 0 ? (
                  <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-gray-400">
                    <ChevronLeft size={16} className="mr-1" /> Back
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={onSkip} className="text-gray-400" data-testid="wizard-skip">
                    Skip for now
                  </Button>
                )}
                <Button 
                  onClick={handleNext}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="wizard-next"
                >
                  {step === 0 && features.storefront && !hasVendorAccount ? 'Next' : 'Finish Setup'} <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
